import { IS_PRODUCTION, WEBSITE_URL } from "@/features/config";
import { useFeatureFlags } from "@/features/config/useFeatureFlags";
import pixelArxiv from "@/features/home/assets/pixel-arxiv.png";
import pixelBiorxiv from "@/features/home/assets/pixel-biorxiv.png";
import pixelDoc from "@/features/home/assets/pixel-doc.png";
import pixelChembl from "@/features/home/assets/pixel-chembl.png";
import pixelCtgov from "@/features/home/assets/pixel-ctgov.png";
import pixelFda from "@/features/home/assets/pixel-fda.png";
import pixelGlobe from "@/features/home/assets/pixel-globe.png";
import pixelMedrxiv from "@/features/home/assets/pixel-medrxiv.png";
import pixelPatents from "@/features/home/assets/pixel-patents.png";
import pixelPdb from "@/features/home/assets/pixel-pdb.png";
import pixelPmc from "@/features/home/assets/pixel-pmc.png";
import pixelUniprot from "@/features/home/assets/pixel-uniprot.png";
import { FALLBACK_BLOG_POSTS } from "@/features/home/constants/fallbackBlogPosts";
import { PINNED_BLOG_POST } from "@/features/home/constants/pinnedBlogPost";
import { useBlogFeed } from "@/features/home/hooks/useBlogFeed";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { Link, useSearchParams } from "react-router-dom";

/**
 * Hero showcase: types a paperclip command, then renders a stylized
 * visualization of what that command actually returns.
 */

const Line = ({
  i,
  className = "",
  children,
}: {
  i: number;
  className?: string;
  children: ReactNode;
}) => (
  <div className={`hero-line ${className}`} style={{ animationDelay: `${40 + i * 45}ms` }}>
    {children}
  </div>
);

const Mark = ({ children }: { children: ReactNode }) => (
  <mark className="hero-mark">{children}</mark>
);

const Dim = ({ children }: { children: ReactNode }) => (
  <span className="text-zinc-400">{children}</span>
);

/* -- search: a ranking with relevance scores ------------------------------- */

const SEARCH_HITS = [
  {
    score: 0.94,
    title: "Drugging the undruggable RAS: Mission Possible?",
    path: "/papers/pmc/PMC4136781",
    before: "…the ",
    after: " mutation is the single most common RAS alteration in human cancer…",
  },
  {
    score: 0.89,
    title: "MRTX1133, a noncovalent inhibitor of KRAS G12D",
    path: "/papers/pmc/PMC9870632",
    before: "…selective, nanomolar inhibition of ",
    after: " signaling in pancreatic models…",
  },
  {
    score: 0.81,
    title: "Pan-RAS inhibition in G12D-mutant PDAC",
    path: "/papers/biorxiv/bio_c81d42",
    before: "…tumor regression in ",
    after: "-driven xenografts at tolerated doses…",
  },
  {
    score: 0.74,
    title: "Immune targeting of KRAS G12D neoantigens",
    path: "/papers/pmc/PMC8592011",
    before: "…HLA-C*08:02-restricted T cells recognizing ",
    after: "…",
  },
];

const SearchResult = () => (
  <div className="hero-mono text-sm leading-6 text-zinc-700">
    <Line i={0}>
      <Dim>4 results · Ranked by relevance · 0.38s</Dim>
    </Line>
    <div className="mt-2 space-y-2.5">
      {SEARCH_HITS.map((hit, i) => (
        <Line i={i + 1} key={hit.path} className="grid grid-cols-[1.25rem_5rem_1fr] gap-x-3 sm:grid-cols-[1.25rem_8rem_1fr]">
          <Dim>{i + 1}</Dim>
          <div className="pt-[5px]">
            <div className="hero-score">
              <div
                className="hero-score-fill"
                style={{
                  width: `${hit.score * 100}%`,
                  animationDelay: `${260 + i * 110}ms`,
                }}
              />
            </div>
            <div className="mt-1 text-2xs leading-4 text-zinc-400">{hit.score.toFixed(2)}</div>
          </div>
          <div>
            <span className="text-zinc-900">{hit.title}</span>{" "}
            <Dim><span className="break-all">{hit.path}</span></Dim>
            <div>
              <Dim>{hit.before}</Dim>
              <Mark>KRAS G12D</Mark>
              <Dim>{hit.after}</Dim>
            </div>
          </div>
        </Line>
      ))}
    </div>
  </div>
);

/* -- grep: wildcard matches inside real regulatory text ------------------- */

const Skel = ({ w }: { w: string }) => (
  <div className="hero-skel" style={{ width: w }} />
);

const GREP_DOCS = [
  {
    path: "ondansetron/S-031.txt",
    before: "…ECG changes including ",
    after: " interval prolongation…",
  },
  {
    path: "citalopram/S-049.txt",
    before: "…a mean increase in ",
    after: " of 8.5 msec at 20 mg…",
  },
  {
    path: "methadone/S-021.txt",
    before: "…cases of ",
    after: " prolongation and torsades…",
  },
  {
    path: "moxifloxacin/S-060.txt",
    before: "…patients with known ",
    after: " prolongation…",
  },
  {
    path: "haloperidol/S-042.txt",
    before: "…dose-dependent ",
    after: " effects at IV doses…",
  },
  {
    path: "sotalol/S-027.txt",
    before: "…monitor the ",
    after: " interval at initiation…",
  },
];

const GrepResult = () => (
  <div className="hero-mono">
    <div className="grid max-w-2xl grid-cols-2 gap-3 sm:grid-cols-3">
      {GREP_DOCS.map((doc, i) => (
        <div
          key={doc.path}
          className="hero-doc"
          style={{ animationDelay: `${120 + i * 140}ms` }}
        >
          <p className="truncate text-3xs leading-4 text-zinc-400">/fda/label/{doc.path}</p>
          <div className="mt-1.5 space-y-1.5">
            <Skel w="92%" />
            <Skel w="78%" />
            <Skel w="86%" />
          </div>
          <p className="mt-2 text-3xs leading-4">
            <Dim>{doc.before}</Dim>
            <Mark>QTc</Mark>
            <Dim>{doc.after}</Dim>
          </p>
          <div className="mt-2 space-y-1.5">
            <Skel w="88%" />
            <Skel w="64%" />
          </div>
        </div>
      ))}
    </div>
    <Line i={8} className="mt-3 text-sm leading-6">
      <Dim>214 matches · 63 documents · 0.9s</Dim>
    </Line>
  </div>
);

/* -- cat: the document itself, rendered as a page -------------------------- */

const CatResult = () => (
  <div className="hero-line" style={{ animationDelay: "140ms" }}>
    <div className="hero-page max-w-xl">
      <p className="text-2xs tracking-wide text-zinc-400">
        Nature Reviews Drug Discovery · 13, 828–851 (2014)
      </p>
      <h3 className="font-display mt-2 text-2xl leading-tight tracking-tight text-zinc-900">
        Drugging the undruggable RAS: Mission Possible?
      </h3>
      <p className="mt-1.5 text-sm italic text-zinc-500">
        Adrienne D. Cox, Stephen W. Fesik, Alec C. Kimmelman, Ji Luo &amp; Channing J. Der
      </p>
      <div className="mt-4 grid grid-cols-[1fr_11rem] gap-6">
        <div>
          <p className="text-3xs font-medium uppercase tracking-[0.18em] text-zinc-400">
            Abstract
          </p>
          <p className="mt-1.5 text-sm leading-6 text-zinc-600">
            Despite more than three decades of intensive effort, no effective
            pharmacological inhibitors of the RAS oncoproteins have reached the
            clinic, prompting the widely held perception that RAS proteins are
            "undruggable"…
          </p>
        </div>
        <figure className="border-l border-zinc-200/80 pl-5">
          <svg viewBox="0 0 120 84" className="w-full" aria-hidden>
            <g fill="currentColor" className="text-zinc-800">
              <rect x="8" y="14" width="24" height="58" />
              <rect x="48" y="52" width="24" height="20" />
              <rect x="88" y="64" width="24" height="8" />
            </g>
            <line x1="2" y1="72.5" x2="118" y2="72.5" stroke="currentColor" strokeWidth="1" className="text-zinc-400" />
          </svg>
          <figcaption className="mt-2 text-3xs leading-4 text-zinc-400">
            Fig. 1 | RAS-mutant cancers by isoform: KRAS 85%, NRAS 12%, HRAS 3%.
          </figcaption>
        </figure>
      </div>
      <p className="mt-4 text-right text-3xs text-zinc-300">1 / 24</p>
    </div>
  </div>
);

/* -- map: one question fanned out across hundreds of documents ------------ */

/**
 * Fan-out tree: a trunk drops from the command, then splits into angled
 * branches that each draw toward a document below. Rendered with
 * preserveAspectRatio="none" so branch tips land on the centers of a
 * gapless N-column grid; non-scaling strokes keep the lines hairline.
 */
const MapFanTree = ({
  count,
  delayMs = 200,
  stepMs = 140,
  className = "",
}: {
  count: number;
  delayMs?: number;
  stepMs?: number;
  className?: string;
}) => {
  const height = 32;
  const forkY = 11;
  return (
    <svg
      viewBox={`0 0 100 ${height}`}
      preserveAspectRatio="none"
      className={`hero-fan-tree ${className}`}
      aria-hidden
    >
      <line
        x1="50"
        y1="0"
        x2="50"
        y2={forkY}
        className="hero-branch"
        style={{ animationDelay: `${delayMs}ms` }}
      />
      {Array.from({ length: count }, (_, i) => (
        <line
          key={i}
          x1="50"
          y1={forkY}
          x2={((i + 0.5) / count) * 100}
          y2={height}
          className="hero-branch"
          style={{ animationDelay: `${delayMs + 140 + i * stepMs}ms` }}
        />
      ))}
    </svg>
  );
};

const MAP_DOCS = [
  { doc: "PMC9870632", compound: "MRTX1133", value: "0.2 nM", target: "KRAS G12D" },
  { doc: "PMC8399114", compound: "sotorasib", value: "12 nM", target: "KRAS G12C" },
  { doc: "PMC7614218", compound: "adagrasib", value: "8.4 nM", target: "KRAS G12C" },
  { doc: "bio_9f22c1", compound: "RMC-6236", value: "0.4 nM", target: "pan-RAS" },
  { doc: "PMC9124007", compound: "BI-2865", value: "3.1 nM", target: "pan-KRAS" },
];

const MAP_TOTAL = 512;
const MAP_COUNT_MS = 4200;

const MapResult = () => {
  const [done, setDone] = useState(0);

  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setDone(MAP_TOTAL);
      return;
    }
    let frame = 0;
    const start = performance.now();
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / MAP_COUNT_MS);
      const eased = 1 - (1 - t) ** 2.2;
      setDone(Math.round(MAP_TOTAL * eased));
      if (t < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, []);

  return (
    <div className="hero-mono">
      <Line i={0} className="text-sm leading-6">
        <Dim>Spawning 512 agents · One per document · Asking </Dim>
        <span className="text-zinc-900">"extract IC50"</span>
      </Line>
      <div className="mt-3 max-w-3xl">
        <MapFanTree count={MAP_DOCS.length} delayMs={300} stepMs={200} className="hidden sm:block" />
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-5 sm:gap-0">
          {MAP_DOCS.map((doc, i) => (
            <div key={doc.doc} className="sm:px-1.5">
              <div
                className="hero-spawn-stem sm:hidden"
                style={{ animationDelay: `${360 + i * 200}ms` }}
                aria-hidden
              />
              <div
                className="hero-doc hero-spawn"
                style={{ animationDelay: `${420 + i * 200}ms` }}
              >
                <p className="truncate text-3xs leading-4 text-zinc-400">{doc.doc}</p>
                <p className="mt-1 text-3xs italic leading-4 text-zinc-400">Extract IC50?</p>
                <div
                  className="hero-answer mt-2 text-3xs leading-5"
                  style={{ animationDelay: `${1150 + i * 200}ms` }}
                >
                  <p className="text-zinc-900">{doc.compound}</p>
                  <p className="mt-0.5">
                    <Mark>IC50 {doc.value}</Mark>
                  </p>
                  <p className="mt-0.5 text-zinc-400">{doc.target}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      <Line i={10} className="mt-3 text-sm leading-6">
        <span className="hero-progress" aria-hidden>
          <span className="hero-progress-fill" />
        </span>
        <Dim>
          {" "}
          {done}/{MAP_TOTAL} documents answered
        </Dim>
      </Line>
    </div>
  );
};

/* -- stats variant: the corpus by the numbers ------------------------------ */

type PixelSource = {
  name: string;
  icon: string;
  count: string;
  kind: string;
  years: string;
  updated: string;
  about: string;
  use: string;
};

const PIXEL_SOURCES: PixelSource[] = [
  {
    name: "PMC", icon: pixelPmc, count: "7.5M",
    kind: "full text", years: "all years", updated: "weekly",
    about: "Open-access papers from PubMed Central, including Nature, Science, Cell, NEJM and The Lancet. Parsed to line-addressable text with figures and supplements.",
    use: "search -s pmc · /papers/pmc/",
  },
  {
    name: "bioRxiv", icon: pixelBiorxiv, count: "388K",
    kind: "full text", years: "2013–present", updated: "weekly",
    about: "Preprints in the biological sciences, operated by Cold Spring Harbor Laboratory. Linked to the published version when one exists.",
    use: "search -s biorxiv · /papers/biorxiv/",
  },
  {
    name: "medRxiv", icon: pixelMedrxiv, count: "82K",
    kind: "full text", years: "2019–present", updated: "weekly",
    about: "Preprints in the health and clinical sciences, including the trials and cohort studies that reach journals months later.",
    use: "search -s medrxiv · /papers/medrxiv/",
  },
  {
    name: "arXiv", icon: pixelArxiv, count: "3.0M",
    kind: "full text", years: "1991–present", updated: "weekly",
    about: "Every arXiv category, with PDFs parsed by OCR so equations, tables and figures survive. Where machine learning meets biology.",
    use: "search -s arxiv · /papers/arxiv/",
  },
  {
    name: "Abstracts", icon: pixelDoc, count: "200M",
    kind: "abstracts", years: "all years", updated: "weekly",
    about: "Titles and abstracts for 200M papers, assembled from Crossref and PubMed. The long tail beyond open-access full text; every result carries a live citation count.",
    use: "search -s abstracts_only",
  },
  {
    name: "FDA", icon: pixelFda, count: "225K",
    kind: "full text", years: "all years", updated: "monthly",
    about: "Drug approval reviews, labeling, CBER documents and advisory committee materials from the US FDA, plus Japan PMDA review reports and EU EPARs.",
    use: "search -s fda · /fda/us/ · /fda/jp/ · /fda/eu/",
  },
  {
    name: "ClinicalTrials\u200B.gov", icon: pixelCtgov, count: "580K",
    kind: "structured + full text", years: "all years", updated: "monthly",
    about: "The US registry: protocols, arms, eligibility, outcomes and posted results, readable as files and queryable as tables.",
    use: "search -s trials/us · /clinicaltrials/us/",
  },
  {
    name: "Intl. registries", icon: pixelGlobe, count: "430K",
    kind: "structured", years: "all years", updated: "monthly",
    about: "Thirteen registries beyond the US: EudraCT and CTIS, ISRCTN, ChiCTR, UMIN and jRCT, and the WHO ICTRP roll-up.",
    use: "search -s trials/intl · /clinicaltrials/",
  },
  {
    name: "Patents", icon: pixelPatents, count: "170M",
    kind: "publications", years: "all years", updated: "monthly",
    about: "Patent publications from 75 jurisdictions via Google Patents, with 1.8M full texts and 30M compound annotations.",
    use: "search -s patents · /patents/",
  },
  {
    name: "UniProt", icon: pixelUniprot, count: "575K",
    kind: "structured", years: "Swiss-Prot", updated: "each release",
    about: "Reviewed protein sequences and annotations from Swiss-Prot: function, domains, variants and cross-references.",
    use: "search -s proteins · sql · /proteins/",
  },
  {
    name: "PDB", icon: pixelPdb, count: "256K",
    kind: "structured", years: "all years", updated: "each release",
    about: "Experimentally determined macromolecular structures from the RCSB Protein Data Bank, joined to UniProt accessions.",
    use: "search -s proteins · sql · /proteins/",
  },
  {
    name: "ChEMBL", icon: pixelChembl, count: "2.9M",
    kind: "structured", years: "v37", updated: "each release",
    about: "Bioactive molecules with drug-like properties: compounds, targets, assays and measured activities.",
    use: "search -s proteins · sql · /proteins/",
  },
];

const cap = (text: string) => text.charAt(0).toUpperCase() + text.slice(1);

/** The sources grid; `from` offsets the staggered line reveal so it can sit
 * under other content. */
const SourcesGrid = ({ from = 0 }: { from?: number }) => {
  const [open, setOpen] = useState<number | null>(null);
  const selected = open === null ? null : PIXEL_SOURCES[open];
  return (
    <div>
      <Line i={from} className="font-ui text-sm leading-6">
        <Dim>12 sources · 385M+ documents</Dim>
      </Line>
      {/* The open source's note sits above the grid, between the count and the
          tiles, so it never abuts the composer below. */}
      {selected ? (
        <div className="hero-source-detail max-w-3xl" key={selected.name}>
          <div className="font-ui flex flex-wrap items-baseline gap-x-3 text-xs leading-5 text-zinc-400">
            <span className="text-zinc-900">{selected.name}</span>
            <span>{cap(selected.kind)}</span>
            <span>·</span>
            <span>{cap(selected.years)}</span>
            <span>·</span>
            <span>Updated {selected.updated}</span>
          </div>
          <p className="mt-2 max-w-2xl text-sm leading-6 text-zinc-600">{selected.about}</p>
          <p className="hero-mono mt-2 text-2xs leading-5 text-zinc-500">{selected.use}</p>
        </div>
      ) : (
        <p className="mt-2 text-sm leading-5 text-zinc-400">
          Select a source for a short description
        </p>
      )}
      <div className="mt-5 grid max-w-3xl grid-cols-3 gap-x-4 gap-y-8 sm:grid-cols-4 lg:grid-cols-6">
        {PIXEL_SOURCES.map((source, i) => (
          <Line i={from + i + 1} key={source.name}>
            <button
              type="button"
              aria-expanded={open === i}
              onClick={() => setOpen(open === i ? null : i)}
              className={`hero-source-tile${open === i ? " is-open" : ""}`}
            >
              <span
                className="hero-source-icon"
                role="img"
                aria-label={source.name}
                style={{
                  WebkitMaskImage: `url(${source.icon})`,
                  maskImage: `url(${source.icon})`,
                }}
              />
              <span className="font-ui text-base leading-5 text-zinc-900">{source.name}</span>
              <span className="font-ui text-2xs leading-4 text-zinc-500">{source.count}</span>
            </button>
          </Line>
        ))}
      </div>
    </div>
  );
};

const SourcesResult = () => <SourcesGrid />;

/* -- install: the two-command setup, straight from the install page -------- */

/** A one-line command box with a quiet copy action at its end. */
const CommandBox = ({ command }: { command: string }) => {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    await navigator.clipboard.writeText(command);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1600);
  };
  return (
    <div className="hero-code mt-1.5 !flex items-center justify-between gap-6">
      <span className="overflow-x-auto whitespace-pre">{command}</span>
      <button
        type="button"
        onClick={copy}
        aria-label={copied ? "Copied" : `Copy “${command}”`}
        className="shrink-0 font-mono text-2xs uppercase tracking-[0.12em] text-zinc-400 transition-colors hover:text-zinc-900"
      >
        {copied ? "Copied" : "Copy"}
      </button>
    </div>
  );
};

const InstallSteps = () => (
  <div className="max-w-2xl">
    <div className="space-y-4">
      <Line i={0}>
        <p className="text-sm leading-5 text-zinc-500">1 · Install the CLI</p>
        <CommandBox command="curl -fsSL https://paperclip.gxl.ai/install.sh | bash" />
      </Line>
      <Line i={1}>
        <p className="text-sm leading-5 text-zinc-500">
          2 · Teach your agent — writes the skill file into your project
        </p>
        <CommandBox command="paperclip install" />
      </Line>
    </div>
    <Line i={2} className="mt-4 text-sm leading-6">
      <Dim>No terminal? Point any MCP client at </Dim>
      <span className="hero-mono text-sm text-zinc-900">https://paperclip.gxl.ai/mcp</span>
    </Line>
    <Line i={3} className="mt-2 text-sm leading-6">
      <Dim>More · </Dim>
      <Link
        to="/install"
        className="text-zinc-900 underline decoration-zinc-300 underline-offset-4 hover:decoration-zinc-700"
      >
        Setup guides for Claude Code, Cursor, Codex, ChatGPT and MCP clients →
      </Link>
    </Line>
  </div>
);

const InstallResult = () => <InstallSteps />;

/* -- cite: BibTeX for the software, with a copy action ---------------------- */

const BIBTEX = `@software{paperclip2026,
  author = {{Generative Expert Labs}},
  title  = {Paperclip: an agent-native filesystem for scientific literature},
  year   = {2026},
  url    = {https://paperclip.gxl.ai}
}`;

const CiteResult = () => {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    await navigator.clipboard.writeText(BIBTEX);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1600);
  };
  return (
    <div className="max-w-2xl">
      <Line i={0} className="text-sm leading-6">
        <Dim>If Paperclip contributed to your work, please cite it</Dim>
      </Line>
      <Line i={1} className="mt-3">
        <div className="hero-code !flex items-start justify-between gap-6">
          <pre className="m-0 overflow-x-auto whitespace-pre font-mono text-inherit">{BIBTEX}</pre>
          <button
            type="button"
            onClick={copy}
            className="shrink-0 font-mono text-2xs uppercase tracking-[0.12em] text-zinc-400 transition-colors hover:text-zinc-900"
          >
            {copied ? "Copied" : "Copy"}
          </button>
        </div>
      </Line>
    </div>
  );
};

/* -- blogs: the latest posts, same feed as the old home page --------------- */

const formatBlogDate = (date: Date): string =>
  date.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });

const BlogsResult = () => {
  const { data, isError } = useBlogFeed();
  const posts =
    !isError && data && data.length >= 2
      ? [...data.slice(0, 2), PINNED_BLOG_POST]
      : FALLBACK_BLOG_POSTS;

  return (
    <div className="max-w-2xl text-sm leading-6">
      <Line i={0} className="font-ui text-sm">
        <Dim>{posts.length} posts · {WEBSITE_URL.replace("https://", "")}/blog</Dim>
      </Line>
      <div className="mt-2 space-y-1.5">
        {posts.map((post, i) => (
          <Line i={i + 1} key={post.url} className="grid grid-cols-[1.25rem_minmax(0,1fr)] gap-x-3">
            <Dim>{i + 1}</Dim>
            <span className="flex flex-wrap items-baseline gap-x-3">
              <a
                href={post.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-zinc-900 underline decoration-zinc-300 underline-offset-4 hover:decoration-zinc-700"
              >
                {post.title}
              </a>
              <span className="font-ui text-xs text-zinc-400">{formatBlogDate(post.pubDate)}</span>
            </span>
          </Line>
        ))}
      </div>
      <Line i={5} className="mt-3 pl-[2rem]">
        <a
          href={`${WEBSITE_URL}/blog`}
          target="_blank"
          rel="noopener noreferrer"
          className="font-ui text-zinc-900 underline decoration-zinc-300 underline-offset-4 hover:decoration-zinc-700"
        >
          View all posts →
        </a>
      </Line>
      <Line i={6} className="mt-4 text-sm leading-6">
        <Dim>
          GXL builds the infrastructure that lets AI agents work reliably at the frontier of
          science and medicine. Founded by Stanford Professor James Zou and entrepreneur
          Christine Lemke ·{" "}
        </Dim>
        <a
          href="https://gxl.ai"
          target="_blank"
          rel="noopener noreferrer"
          className="text-zinc-900 underline decoration-zinc-300 underline-offset-4 hover:decoration-zinc-700"
        >
          gxl.ai
        </a>
      </Line>
    </div>
  );
};

/* -- pricing: the plans, set as a hairline grid ----------------------------- */
// Numbers mirror features/pricing/pages/PricingPage.tsx; keep them in step.

const PRICING_PLANS = [
  { name: "Free", price: "$0", documents: "100", perMinute: "25", queue: "Standard", datasets: "5 total · 50 rows" },
  { name: "Pro plan", price: "$200", documents: "10,000", perMinute: "50", queue: "Priority", datasets: "20 / mo · 200 rows" },
] as const;

const PRICING_ROWS: Array<{ label: string; key: "documents" | "perMinute" | "queue" | "datasets" }> = [
  { label: "Map documents / day", key: "documents" },
  { label: "Ops started / min", key: "perMinute" },
  { label: "Command queue", key: "queue" },
  { label: "Datasets · rows shown", key: "datasets" },
];

const PricingResult = () => (
  <div className="max-w-2xl">
    <div className="font-ui mt-1 grid grid-cols-[14rem_repeat(2,minmax(0,1fr))] gap-x-3 text-xs leading-5 sm:gap-x-5">
      <div className="pb-3" aria-hidden />
      {PRICING_PLANS.map((plan) => (
        <Line key={plan.name} i={1} className="pb-3">
          <p className="font-display text-xl leading-none tracking-tight text-zinc-900">{plan.name}</p>
          <p className="mt-2">
            {plan.price === "$0" ? <span className="text-zinc-900">Free</span> : <Mark>{plan.price}</Mark>}
            {plan.price !== "$0" && <Dim> / mo</Dim>}
          </p>
        </Line>
      ))}

      {PRICING_ROWS.map((row, i) => (
        <div key={row.key} className="contents">
          <Line i={i + 2} className="border-t border-zinc-200/70 py-2 text-zinc-400">
            {row.label}
          </Line>
          {PRICING_PLANS.map((plan) => (
            <Line key={plan.name} i={i + 2} className="border-t border-zinc-200/70 py-2 text-zinc-900">
              {plan[row.key]}
            </Line>
          ))}
        </div>
      ))}
    </div>

    <Line i={6} className="mt-4 text-sm leading-6 text-zinc-500">
      Pro plan lifts every Free-plan limit, on the priority queue. Need more —{" "}
      <a href="mailto:learn@gxl.ai?subject=Paperclip%20Enterprise" className="font-ui text-zinc-900 underline decoration-zinc-300 underline-offset-4 hover:decoration-zinc-700">
        Contact sales
      </a>
      .
    </Line>
    <Line i={7} className="mt-1 text-sm leading-6">
      <Link
        to="/pricing"
        className="font-ui text-zinc-900 underline decoration-zinc-300 underline-offset-4 hover:decoration-zinc-700"
      >
        See full pricing →
      </Link>
    </Line>
  </div>
);

/* -- speedtest: real grep benchmarks raced against a sequential scan ------- */

// Timings are the published slab-grep benchmarks (see the architecture
// dispatch): both lanes animate in real time, so paperclip finishes in
// under ~1s while every sequential lane is still crawling when the scene ends.
const RACE_START_DELAY_MS = 500;

const RACES = [
  {
    query: '"intracellular tachyzoites"',
    corpus: "470K papers · bioRxiv + medRxiv",
    fast: 0.72,
    slow: 26.4,
  },
  {
    query: '"binding affinity.*nanomolar"',
    corpus: "7.5M papers · PubMed Central",
    fast: 0.24,
    slow: 70.1,
  },
  {
    query: '"KRAS G12D inhibitor resistance"',
    corpus: "PMC + FDA NDA packages",
    fast: 1.1,
    slow: 41.8,
  },
];

const SpeedTestResult = () => {
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setElapsed(2);
      return;
    }
    let frame = 0;
    const start = performance.now();
    const tick = (now: number) => {
      setElapsed(Math.max(0, (now - start - RACE_START_DELAY_MS) / 1000));
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, []);

  const allDone = elapsed >= Math.max(...RACES.map((race) => race.fast));

  return (
    <div className="max-w-2xl">
      <Line i={0} className="text-sm leading-6">
        <Dim>3 queries · slab-grep vs sequential scan · Same corpus, same regex, real time</Dim>
      </Line>

      <div className="mt-4 space-y-5">
        {RACES.map((race, gi) => {
          const done = elapsed >= race.fast;
          return (
            <Line i={gi + 1} key={race.query}>
              <div className="flex flex-wrap items-baseline gap-x-3 text-xs leading-5">
                <span className="hero-mono text-xs text-zinc-900">grep {race.query}</span>
                <Dim>{race.corpus}</Dim>
              </div>
              <div className="mt-1.5 grid grid-cols-[5.5rem_1fr_10rem] items-center gap-x-3 text-2xs leading-4">
                <Dim>Paperclip</Dim>
                <div className="hero-race-track">
                  <div
                    className="hero-race-fill hero-race-fast"
                    style={{ animationDuration: `${race.fast}s` }}
                  />
                </div>
                <span>{done ? <Mark>Done · {race.fast.toFixed(2)} s</Mark> : <Dim>Running…</Dim>}</span>
              </div>
              <div className="mt-1 grid grid-cols-[5.5rem_1fr_10rem] items-center gap-x-3 text-2xs leading-4">
                <Dim>Sequential</Dim>
                <div className="hero-race-track">
                  <div
                    className="hero-race-fill hero-race-slow"
                    style={{ animationDuration: `${race.slow}s` }}
                  />
                </div>
                <Dim>
                  {Math.min(elapsed, race.slow).toFixed(1)} s · est. {race.slow.toFixed(0)} s
                </Dim>
              </div>
            </Line>
          );
        })}
      </div>

      <Line i={5} className="mt-5 text-sm leading-6">
        <span className={allDone ? undefined : "invisible"}>
          <span className="text-zinc-900">Up to 292× faster</span>
          <Dim> — median regex latency &lt;350 ms across 2,400 benchmark queries</Dim>
        </span>
      </Line>
    </div>
  );
};

/* -------------------------------------------------------------------------- */

const SCENE_ROTATE_MS = 5000;

type Scene = {
  command: string;
  label: string;
  /** Included in the auto-rotation; others are reachable only via the nav. */
  auto?: boolean;
  result: () => ReactNode;
};

const COMMAND_SCENES: Scene[] = [
  { command: 'paperclip search "KRAS G12D" -s pmc', label: "search", auto: true, result: SearchResult },
  { command: 'paperclip grep -i "QTc" /fda/', label: "grep", auto: true, result: GrepResult },
  { command: "paperclip cat /papers/pmc/PMC4136781", label: "cat", auto: true, result: CatResult },
  { command: 'paperclip map --from s_01 "extract IC50"', label: "map", auto: true, result: MapResult },
];

const STATS_SCENES: Scene[] = [
  { command: "paperclip sources", label: "sources", auto: true, result: SourcesResult },
  { command: "paperclip how to install", label: "install", auto: true, result: InstallResult },
  { command: "paperclip list blogs", label: "blogs", auto: true, result: BlogsResult },
  { command: "paperclip cite", label: "cite", result: CiteResult },
  { command: "paperclip pricing", label: "pricing", result: PricingResult },
  { command: "paperclip speedtest grep", label: "speed test", result: SpeedTestResult },
];

// Default hero: install first, then sources, blogs and cite as clickable
// panels — the page leads with the CLI install rather than chat. The
// command visualizations (`?hero=commands`) and the full stats tour
// (`?hero=stats`) are stashed behind query flags for now.
const DEFAULT_SCENES: Scene[] = [
  STATS_SCENES[1], // install
  STATS_SCENES[0], // sources
  ...STATS_SCENES.slice(2, 4), // blogs, cite
];

const SCENE_SETS: Record<string, Scene[]> = {
  commands: COMMAND_SCENES,
  stats: STATS_SCENES,
};

export const PapersGraphic = () => {
  const [searchParams] = useSearchParams();
  const { flags } = useFeatureFlags();
  const heroFlag = searchParams.get("hero") ?? "";
  // Pricing follows the same gate as the /pricing route and nav link: always
  // on outside prod, flag-controlled on prod.
  const showPricing = !IS_PRODUCTION || flags.paperclipPricing;
  const scenes = useMemo(() => {
    const set = SCENE_SETS[heroFlag] ?? DEFAULT_SCENES;
    return showPricing ? set : set.filter((s) => s.label !== "pricing");
  }, [heroFlag, showPricing]);
  // Stashed variants keep the command line as a caption; the default panels
  // don't need one.
  const showCommand = heroFlag in SCENE_SETS;
  const [index, setIndex] = useState(0);
  // Carousel: advance every 5s until the visitor picks a tab themselves;
  // hovering the showcase holds the current panel.
  const [autoRotate, setAutoRotate] = useState(true);
  const [hovered, setHovered] = useState(false);

  useEffect(() => {
    setIndex(0);
  }, [scenes]);

  useEffect(() => {
    if (!autoRotate || hovered || scenes.length < 2) return;
    const tick = window.setInterval(
      () => setIndex((i) => (i + 1) % scenes.length),
      SCENE_ROTATE_MS,
    );
    return () => window.clearInterval(tick);
  }, [autoRotate, hovered, scenes.length]);

  const pick = (i: number) => {
    setAutoRotate(false);
    setIndex(i);
  };

  const scene = scenes[index % scenes.length];
  const Result = scene.result;

  return (
    <div
      className="hero-showcase"
      aria-label="About Paperclip"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onFocusCapture={() => setHovered(true)}
      onBlurCapture={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setHovered(false);
      }}
    >
      {scenes.length > 1 && (
        <div className="hero-scene-nav" role="tablist" aria-label="Sections">
          {scenes.map((s, i) => (
            <button
              key={s.label}
              type="button"
              role="tab"
              aria-selected={i === index % scenes.length}
              className={`hero-scene-tab${i === index % scenes.length ? " is-active" : ""}`}
              onClick={() => pick(i)}
            >
              {s.label}
            </button>
          ))}
        </div>
      )}
      {showCommand && (
        <div className="papers-hero-terminal">
          <span className="papers-hero-prompt" aria-hidden>
            $
          </span>
          <span className="papers-hero-command">{scene.command}</span>
        </div>
      )}
      <div className="hero-result" key={scene.label}>
        <Result />
      </div>
    </div>
  );
};
